import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import { PlusIcon } from "@heroicons/react/20/solid";
import { IncidentStatus } from "@/gql/graphql";
import { Button } from "@/components/catalyst/button";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { MultiSelectFilter } from "@/lib/Components/DataTable/Filters/MultiSelectFilter";
import { SupplierFilter } from "@/lib/Components/DataTable/Filters/SupplierFilter";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import type { IncidentRegisterSearchParams } from "@/app/Incidents/Routes/incidentRoutes";
import { IncidentsTable } from "@/app/Incidents/Components/IncidentsTable";
import { ReportIncidentDialog } from "@/app/Incidents/Components/Dialogs/ReportIncidentDialog";

export function IncidentRegisterScreen() {
  const t = useTranslations("incident");
  const search = useSearch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/incidents",
  });
  const navigate = useNavigate();
  const { isAdmin } = useTenant();
  const { supplierId: globalSupplierId } = useGlobalSupplier();
  const { open: openReportDialog } = useDialog(ReportIncidentDialog);

  const filterVariables = {
    statuses: nonEmpty(search.incidentStatus),
    supplier_id: globalSupplierId ?? search.filterSupplierId,
  };

  function updateSearch(patch: Partial<IncidentRegisterSearchParams>) {
    return navigate({
      from: "/$tenant/$orgId/supplier/{-$supplierId}",
      to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/incidents",
      params: (prev) => prev,
      search: (prev) => ({ ...prev, incidentPage: undefined, ...patch }),
    });
  }

  return (
    <div className="py-4">
      <IncidentsTable
        id="incidents"
        filterVariables={filterVariables}
        search={search.incidentSearch ?? ""}
        onSearchChange={(value) =>
          updateSearch({ incidentSearch: value || undefined })
        }
        page={search.incidentPage ?? 1}
        onPageChange={(page) =>
          navigate({
            from: "/$tenant/$orgId/supplier/{-$supplierId}",
            to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/incidents",
            params: (prev) => prev,
            search: (prev) => ({ ...prev, incidentPage: page }),
          })
        }
        filters={
          <div className="flex flex-wrap gap-2">
            <MultiSelectFilter
              label={t("filters.status")}
              options={Object.values(IncidentStatus).map((status) => ({
                label: t(`status.${status}`),
                value: status,
              }))}
              selected={search.incidentStatus ?? []}
              onChange={(incidentStatus) => updateSearch({ incidentStatus })}
            />
            {isAdmin && !globalSupplierId ? (
              <SupplierFilter
                label={t("filters.supplier")}
                selected={search.filterSupplierId ?? null}
                onChange={(supplierId) =>
                  updateSearch({ filterSupplierId: supplierId ?? undefined })
                }
              />
            ) : null}
          </div>
        }
        rightButtons={
          isAdmin ? (
            <Button LeadingIcon={PlusIcon} onClick={() => openReportDialog({})}>
              {t("actions.reportIncident")}
            </Button>
          ) : null
        }
      />
    </div>
  );
}

function nonEmpty<T>(values: T[] | undefined) {
  return values && values.length > 0 ? values : undefined;
}
